import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Settings, MessageSquare, PenTool, Briefcase, FileCode, ArrowLeft, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ProLoginGate } from './pro/ProLoginGate';
import { SettingsPanel } from './pro/SettingsPanel';
import { SetupAssistantPanel } from './pro/SetupAssistantPanel';
import { MarketingPanel } from './pro/MarketingPanel';
import { BusinessCoachPanel } from './pro/BusinessCoachPanel';
import { DocGeneratorPanel } from './pro/DocGeneratorPanel';
import { UsagePanel } from './pro/UsagePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProDashboard = () => {
  const navigate = useNavigate();
  // pro_email is NOT a credential — it's a display value (same email is on PayPal receipts)
  const [email, setEmail] = useState(localStorage.getItem('pro_email') || '');
  const [apiKey, setApiKey] = useState('');
  const [isSetup, setIsSetup] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [loginCode, setLoginCode] = useState('');
  const [activeFeature, setActiveFeature] = useState('settings');
  const [loading, setLoading] = useState(false);
  const [testingKey, setTestingKey] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [newAccessCode, setNewAccessCode] = useState(null);
  const [newCodeCopied, setNewCodeCopied] = useState(false);
  const [loginNotice, setLoginNotice] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // AI Feature States
  const [setupQ, setSetupQ] = useState('');
  const [setupAnswer, setSetupAnswer] = useState('');
  const [marketingType, setMarketingType] = useState('social');
  const [marketingTopic, setMarketingTopic] = useState('');
  const [marketingResult, setMarketingResult] = useState('');
  const [adviceQ, setAdviceQ] = useState('');
  const [adviceResult, setAdviceResult] = useState(null);
  const [docType, setDocType] = useState('readme');
  const [codeSnippet, setCodeSnippet] = useState('');
  const [docResult, setDocResult] = useState('');

  // Verify session via cookie on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/pro/verify`);
        if (cancelled) return;
        if (res.data?.valid) {
          setIsAuthenticated(true);
          setEmail(res.data.email);
          try {
            const s = await axios.get(`${API}/pro/settings/${encodeURIComponent(res.data.email)}`);
            setIsSetup(!!s.data?.has_api_key);
          } catch {
            setIsSetup(false);
          }
        }
      } catch {
        setIsAuthenticated(false);
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveApiKey = async () => {
    if (!email || !apiKey) { toast.error('Enter your email and OpenAI API key'); return; }
    setLoading(true);
    setTestResult(null);
    try {
      await axios.post(`${API}/pro/settings`, { email, openai_api_key: apiKey });
      setIsSetup(true);
      toast.success('API key saved securely! AI features are now active.');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save. Make sure you have a Pro account.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const testApiKey = async () => {
    setTestingKey(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/ai/test-key`, { user_email: email });
      setTestResult({ ok: true, message: res.data?.message || 'Your OpenAI key is valid and working.' });
      toast.success('OpenAI key is working!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Could not verify the key right now.';
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTestingKey(false);
    }
  };

  const regenerateCode = async () => {
    if (!window.confirm('Rotate your access code now? Your old code will stop working immediately.')) return;
    setRegenerating(true);
    setNewAccessCode(null);
    setNewCodeCopied(false);
    try {
      const res = await axios.post(`${API}/pro/regenerate-access-code`, {});
      setNewAccessCode(res.data.access_code);
      toast.success('New access code generated and emailed to you.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate access code');
    } finally {
      setRegenerating(false);
    }
  };

  const copyNewCode = async () => {
    try {
      await navigator.clipboard.writeText(newAccessCode);
      setNewCodeCopied(true);
      toast.success('Access code copied');
      setTimeout(() => setNewCodeCopied(false), 2000);
    } catch {
      toast.error('Could not copy — please select and copy manually');
    }
  };

  const handleProLogin = async () => {
    if (!email || !loginCode) { toast.error('Enter your email and access code'); return; }
    setLoading(true);
    setLoginNotice(null);
    try {
      const res = await axios.post(`${API}/pro/login`, { email, access_code: loginCode });
      localStorage.setItem('pro_email', res.data.email);
      setIsAuthenticated(true);
      setIsSetup(res.data.has_api_key);
      toast.success('Welcome to Pro Dashboard!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid credentials';
      if (error.response?.status === 429) {
        setLoginNotice("Too many attempts. For your security, please wait a few minutes before trying again.");
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Enter your email first, then click resend');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/pro/resend-access-code`, { email });
      toast.success("If we have a Pro account for that email, the access code is on its way.");
    } catch (error) {
      toast.error('Unable to send right now. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const handleProLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[ProDashboard/logout] server call failed, continuing:', e?.message);
      }
    }
    localStorage.removeItem('pro_email');
    setIsAuthenticated(false);
    setIsSetup(false);
  };

  const fetchUsage = async () => {
    if (!email || !isSetup) return;
    setUsageLoading(true);
    try {
      const res = await axios.get(`${API}/ai/usage/${encodeURIComponent(email)}`);
      setUsage(res.data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[ProDashboard/usage] fetch failed:', e?.message);
      }
    } finally {
      setUsageLoading(false);
    }
  };

  // Load usage once authenticated with a saved key
  useEffect(() => {
    if (isAuthenticated && isSetup) fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isSetup]);

  const callAI = async (endpoint, body, setter) => {
    if (!isSetup) { toast.error('Please save your API key first'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/${endpoint}`, { user_email: email, ...body });
      setter(res.data);
      toast.success('AI response generated!');
      fetchUsage();
    } catch (error) {
      const msg = error.response?.data?.detail || 'AI request failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'setup', label: 'Setup Assistant', icon: MessageSquare },
    { id: 'marketing', label: 'Marketing', icon: PenTool },
    { id: 'advice', label: 'Business Coach', icon: Briefcase },
    { id: 'docs', label: 'Doc Generator', icon: FileCode },
    { id: 'usage', label: 'Usage', icon: BarChart3 },
  ];

  return (
    <div className="relative min-h-screen">
      <div className="circuit-bg" />
      <div className="circuit-overlay" />

      <div className="content-wrapper">
        <nav className="border-b border-green-500/20 backdrop-blur-md" data-testid="pro-nav">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-green-400" data-testid="back-home">
                <ArrowLeft className="w-4 h-4 mr-2" /> Home
              </Button>
              <span className="text-lg font-bold text-green-400">PRO DASHBOARD</span>
              {isAuthenticated && (
                <Button onClick={handleProLogout} variant="ghost" className="text-red-400 hover:text-red-300" data-testid="pro-logout">
                  Logout
                </Button>
              )}
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {!isAuthenticated ? (
            <ProLoginGate
              email={email}
              setEmail={setEmail}
              loginCode={loginCode}
              setLoginCode={setLoginCode}
              handleProLogin={handleProLogin}
              handleResendCode={handleResendCode}
              loading={loading}
              navigate={navigate}
              loginNotice={loginNotice}
            />
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-8" data-testid="pro-tabs">
                {features.map(f => (
                  <Button key={f.id} onClick={() => setActiveFeature(f.id)}
                    className={`${activeFeature === f.id ? 'btn-primary' : 'btn-secondary'} px-4 py-2`}
                    data-testid={`pro-tab-${f.id}`}>
                    <f.icon className="w-4 h-4 mr-2" /> {f.label}
                  </Button>
                ))}
              </div>

              {activeFeature === 'settings' && (
                <SettingsPanel
                  email={email}
                  setEmail={setEmail}
                  apiKey={apiKey}
                  setApiKey={setApiKey}
                  saveApiKey={saveApiKey}
                  loading={loading}
                  isSetup={isSetup}
                  testKey={testApiKey}
                  testingKey={testingKey}
                  testResult={testResult}
                  regenerateCode={regenerateCode}
                  regenerating={regenerating}
                  newAccessCode={newAccessCode}
                  copyNewCode={copyNewCode}
                  newCodeCopied={newCodeCopied}
                  usage={usage}
                  goToUsage={() => setActiveFeature('usage')}
                />
              )}

              {activeFeature === 'setup' && (
                <SetupAssistantPanel
                  setupQ={setupQ}
                  setSetupQ={setSetupQ}
                  setupAnswer={setupAnswer}
                  onAsk={() => callAI('setup-assistant', { question: setupQ }, (r) => setSetupAnswer(r.answer))}
                  loading={loading}
                />
              )}

              {activeFeature === 'marketing' && (
                <MarketingPanel
                  marketingType={marketingType}
                  setMarketingType={setMarketingType}
                  marketingTopic={marketingTopic}
                  setMarketingTopic={setMarketingTopic}
                  marketingResult={marketingResult}
                  onGenerate={() => callAI('marketing-content', { content_type: marketingType, topic: marketingTopic }, (r) => setMarketingResult(r.content))}
                  loading={loading}
                />
              )}

              {activeFeature === 'advice' && (
                <BusinessCoachPanel
                  adviceQ={adviceQ}
                  setAdviceQ={setAdviceQ}
                  adviceResult={adviceResult}
                  onAsk={() => callAI('business-advice', { question: adviceQ }, setAdviceResult)}
                  loading={loading}
                />
              )}

              {activeFeature === 'docs' && (
                <DocGeneratorPanel
                  docType={docType}
                  setDocType={setDocType}
                  codeSnippet={codeSnippet}
                  setCodeSnippet={setCodeSnippet}
                  docResult={docResult}
                  onGenerate={() => callAI('documentation', { doc_type: docType, code_snippet: codeSnippet }, (r) => setDocResult(r.documentation))}
                  loading={loading}
                />
              )}

              {activeFeature === 'usage' && (
                <UsagePanel
                  usage={usage}
                  usageLoading={usageLoading}
                  refreshUsage={fetchUsage}
                  isSetup={isSetup}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProDashboard;
